import { useMemo } from "react";

import { DOWNLOAD_LINKS, PLATFORM_IDS } from "../../data/siteContent";
import { useLocale } from "../../i18n/LocaleProvider";
import {
  detectOs,
  resolveDownloadHref,
  resolvePlatformDownloadHref,
} from "../../lib/detectOs";
import { trackDownload } from "../../lib/trackDownload";
import { Section } from "../layout/Section";
import { Button } from "../ui/Button";
import { PlatformLogo } from "../ui/PlatformLogo";

import styles from "./Platforms.module.css";

function readNavigatorHints(): { userAgent: string; maxTouchPoints: number } {
  if (typeof navigator === "undefined") {
    return { userAgent: "", maxTouchPoints: 0 };
  }
  return {
    userAgent: navigator.userAgent,
    maxTouchPoints: navigator.maxTouchPoints,
  };
}

export function Platforms() {
  const { copy } = useLocale();

  const hints = useMemo(() => readNavigatorHints(), []);

  const { osDownloadHref, detectedOs } = useMemo(() => {
    const os = detectOs(hints.userAgent);
    return {
      osDownloadHref: resolveDownloadHref(os, DOWNLOAD_LINKS, hints),
      detectedOs: os,
    };
  }, [hints]);

  const trackOsDownload = () => {
    const platform =
      detectedOs === "windows" ||
      detectedOs === "macos" ||
      detectedOs === "linux"
        ? detectedOs
        : "windows";
    trackDownload(platform);
  };

  const platformDownloadHrefs = useMemo(
    () =>
      Object.fromEntries(
        PLATFORM_IDS.map((id) => [
          id,
          resolvePlatformDownloadHref(id, DOWNLOAD_LINKS, hints),
        ]),
      ) as Record<(typeof PLATFORM_IDS)[number], string>,
    [hints],
  );

  return (
    <Section id="platforms" title={copy.platforms.title}>
      <div className={styles.grid}>
        {PLATFORM_IDS.map((id) => {
          const platform = copy.platforms.cards[id];
          return (
            <article key={id} className={styles.card}>
              <PlatformLogo platform={id} />
              <h3 className={styles.cardTitle}>{platform.title}</h3>
              <p className={styles.cardText}>{platform.description}</p>
              <Button
                href={platformDownloadHrefs[id]}
                variant="ghost"
                className={styles.downloadBtn}
                onClick={() => {
                  trackDownload(id);
                }}
              >
                {copy.cta.download}
              </Button>
            </article>
          );
        })}
      </div>

      <p className={styles.note}>{copy.platforms.note}</p>
      <p className={styles.disclaimer}>
        {copy.platforms.distributionDisclaimer}
      </p>

      <div className={styles.mainCta}>
        <Button href={osDownloadHref} onClick={trackOsDownload}>
          {copy.cta.downloadForOs}
        </Button>
      </div>
    </Section>
  );
}
